import {PlatformNavbarMenuItem} from "@flexinet/ui-components";
import {rbacActions} from "../../config/rbac/actions.ts";

/**
 * RBAC-filter a menu tree, nested `links[]` included.
 *
 * Before FNP-1119 both callers filtered only the top level, so a child rendered
 * whenever its parent was allowed — `/aliases` showed under `account-settings`
 * for a user holding GetAccountSettings but not ListAliases.
 *
 * A parent that survives its own check is kept even when every child is filtered
 * out: the parent is a real destination in its own right (`/account-settings`
 * renders a page). The one exception is a parent that is only a grouping handle —
 * `tags-group` has `link: '#'` — which is dropped once it has no visible children,
 * since clicking it goes nowhere.
 *
 * `PlatformNavbarMenuItem.rbac` is typed `string | undefined` by
 * @flexinet/ui-components, so the enum has to be re-asserted at the comparison.
 * getMenuItems() only ever assigns rbacActions members. Tightening the shared
 * type is a library change across four consuming apps, not this ticket's.
 */
export const filterMenuItems = (
    items: PlatformNavbarMenuItem[],
    // string[]: the shared VerifiedPermissionsProvider publishes action ids as
    // plain strings (FNP-1730).
    allowedActions: string[],
): PlatformNavbarMenuItem[] => {

    return items.reduce<PlatformNavbarMenuItem[]>((acc, item) => {

        if (!allowedActions.includes(item.rbac as rbacActions)) {
            return acc;
        }

        if (typeof item.links === 'undefined') {
            acc.push(item);
            return acc;
        }

        const links = filterMenuItems(item.links, allowedActions);

        // A grouping-only parent with nothing left under it leads nowhere.
        if (links.length === 0 && (item.link === '#' || typeof item.link === 'undefined')) {
            return acc;
        }

        acc.push({
            ...item,
            links,
        });

        return acc;
    }, []);
};
